/**
 * S13 — People Memory Admin UI (rota /admin/people-memory)
 * S60 — Adicionada aba "Pipeline" com dashboard agregado por status + reason.
 * ---------------------------------------------------------------------------
 * Tabs:
 *   - Pipeline: dashboard agregado (status + motivos de promoção, todos os grupos).
 *   - Membros: lista de pessoas, contagem por status, toggles de consent.
 *   - Fatos:   lista paginada de syra_person_memories com filtros.
 *   - Funil:   KPIs candidate→awaiting→confirmed/rejected (últimos 30d).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, ListChecks, BarChart3, Activity, ScrollText } from "lucide-react";
import { PeopleMemoryMembers } from "@/components/admin/PeopleMemoryMembers";
import { PeopleMemoryFacts } from "@/components/admin/PeopleMemoryFacts";
import { PeopleMemoryFunnel } from "@/components/admin/PeopleMemoryFunnel";
import { PeopleMemoryPipelineDashboard } from "@/components/admin/PeopleMemoryPipelineDashboard";
import { PromotionAuditTable } from "@/components/admin/PromotionAuditTable";

export default function PeopleMemoryPage() {
  const [groupId, setGroupId] = useState<string>("");
  const [period, setPeriod] = useState<string>("30d");
  const [tab, setTab] = useState<string>("pipeline");

  const { data: groups = [] } = useQuery({
    queryKey: ["admin-people-memory-groups"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("syra_groups")
        .select("id, name")
        .order("name", { ascending: true })
        .limit(200);
      return data ?? [];
    },
  });

  const needsGroupForTab = tab !== "pipeline" && tab !== "audit";

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Memórias de Pessoas</h1>
        <p className="text-sm text-muted-foreground">
          Curadoria dos fatos extraídos sobre membros dos grupos. Apenas o próprio sujeito pode
          confirmar fatos sobre si — admin pode marcar como <em>confirmed_by_admin</em> com justificativa.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pipeline"><Activity className="w-4 h-4 mr-2" />Pipeline</TabsTrigger>
          <TabsTrigger value="audit"><ScrollText className="w-4 h-4 mr-2" />Auditoria</TabsTrigger>
          <TabsTrigger value="members"><Users className="w-4 h-4 mr-2" />Membros</TabsTrigger>
          <TabsTrigger value="facts"><ListChecks className="w-4 h-4 mr-2" />Fatos</TabsTrigger>
          <TabsTrigger value="funnel"><BarChart3 className="w-4 h-4 mr-2" />Funil</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="mt-4">
          <PeopleMemoryPipelineDashboard
            groupId={groupId}
            onGroupChange={setGroupId}
            groups={groups as any}
            period={period}
            onPeriodChange={setPeriod}
          />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <PromotionAuditTable groupId={groupId} groups={groups as any} />
        </TabsContent>

        {needsGroupForTab && (
          <Card className="p-4 mt-4">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Grupo</label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger className="max-w-md">
                <SelectValue placeholder="Selecione um grupo" />
              </SelectTrigger>
              <SelectContent>
                {(groups as any[]).map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>
        )}

        <TabsContent value="members" className="mt-4">
          {!groupId ? (
            <Card className="p-8 text-center text-muted-foreground">Selecione um grupo acima.</Card>
          ) : (
            <PeopleMemoryMembers groupId={groupId} />
          )}
        </TabsContent>
        <TabsContent value="facts" className="mt-4">
          {!groupId ? (
            <Card className="p-8 text-center text-muted-foreground">Selecione um grupo acima.</Card>
          ) : (
            <PeopleMemoryFacts groupId={groupId} />
          )}
        </TabsContent>
        <TabsContent value="funnel" className="mt-4">
          {!groupId ? (
            <Card className="p-8 text-center text-muted-foreground">Selecione um grupo acima.</Card>
          ) : (
            <PeopleMemoryFunnel groupId={groupId} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
